<template>
  <ReaderDrawer v-model="menuOpen">
    <template #header>
      <NuxtLink :to="href.home" class="flex items-center">
        <img
          class="block h-8 w-8"
          src="~/assets/namicomi-logo.svg"
          :alt="config.public.siteName"
        />
        <p
          class="ml-2 font-[Roboto] font-bold text-xl text-nami-deep-blue dark:text-neutral-100"
        >
          {{ config.public.siteName }}
        </p>
      </NuxtLink>
    </template>
    <div class="px-4 space-y-2">
      <div class="grid grid-cols-[1fr_3rem]">
        <div class="space-y-2">
          <h2 class="font-medium text-xl">
            {{ translate("reader.novel.drawer.menu.header.title") }}
          </h2>
          <TheNuxtLink
            class="font-medium text-nami-nami-blue"
            :href="title && linkTo(title)"
          >
            {{ titleTitle }}
          </TheNuxtLink>
        </div>
        <ContextMenuList
          closeOnOutsideClick
          defaultPosition="bottom-left"
          class="relative"
          :options="[
            {
              name: translate('reader.novel.drawer.menu.context.copy_link'),
              icon: IconLink,
              action: getPageLink,
            },
            {
              name: translate('reader.novel.drawer.menu.context.report'),
              icon: IconFlag,
              action: () => {
                if (!loggedIn) appStore.openLoginRequiredModal();
                else showReportModal = true;
              },
            },
          ]"
        >
          <template v-slot="{ isOpen, open, close }">
            <NamiButton
              @buttonClick="() => (isOpen ? close() : open())"
              buttonType="secondary"
              :icon="IconDots"
              :text="!isOpen"
            />
          </template>
        </ContextMenuList>
      </div>
      <div class="space-y-2" v-if="!solo">
        <h2 class="font-medium text-xl">
          {{ translate("reader.novel.drawer.menu.header.chapter") }}
        </h2>
        <NovelChapterSelect
          :currentChapterIndex
          :chapterIdSequence
          :hasNextChapter
          :hasPrevChapter
          :aggregate
          :title
          @switchChapter="(v) => emit('switchChapter', v)"
          @nextChapter="emit('nextChapter')"
          @prevChapter="emit('prevChapter')"
        />
        <NamiButton
          @buttonClick="() => (commentsOpen = !commentsOpen)"
          :icon="commentsOpen ? IconChevronRight : IconChevronLeft"
          :buttonType="commentsOpen ? 'primary' : 'secondary'"
          block
        >
          {{ translate("reader.novel.drawer.menu.button.comments") }}
        </NamiButton>
        <!--      <SelectSingleList-->
        <!--        v-if="currentChapterVariants.length > 1 && !solo"-->
        <!--        class="!mt-4"-->
        <!--        label="Available languages"-->
        <!--        :modelValue="currentChapterId"-->
        <!--        :options="currentChapterVariants"-->
        <!--        :disabled="navLoading || pending"-->
        <!--        @update:modelValue="(v) => v && switchChapter(v)"-->
        <!--      />-->
      </div>
    </div>
    <div class="px-4 space-y-2">
      <h2 class="font-medium">
        {{
          translate("reader.novel.drawer.menu.options.settings_profile.label")
        }}
      </h2>
      <div class="grid grid-cols-[1fr_auto] gap-2">
        <SelectSingleList
          v-model="profileIndex"
          :options="
            profiles.map((profile, index) => ({
              text: translate(profile.profile),
              value: index,
            }))
          "
        />
        <Tooltip
          class="flex items-center"
          variant="contrast"
          text="Reset to defaults"
        >
          <NamiButton
            buttonType="secondary"
            text
            block
            :icon="IconRefresh"
            @buttonClick="resetProfile"
          />
        </Tooltip>
      </div>
    </div>
    <div class="px-4 space-y-2">
      <div class="font-medium">
        {{ translate("reader.novel.drawer.menu.options.theme.label") }}
      </div>
      <NovelThemeSelector
        :themes="availableThemes"
        v-model="settingsModel.templateIndex"
      />
      <div class="font-medium">
        {{
          translate("reader.novel.drawer.menu.options.font_appearance.label")
        }}
      </div>
      <SelectSingleList v-model="fontFamily" :options="fonts">
        <template #option="{ active, option, selected }">
          <SelectSingleListOption
            :style="{
              fontFamily: option.value,
              fontSize: option.props?.baseFontRatio
                ? option.props?.baseFontRatio + 'em'
                : undefined,
            }"
            :active="active"
            :option="option"
            :selected="selected"
          />
        </template>
      </SelectSingleList>
      <div class="flex items-center">
        <span class="font-medium">{{
          translate("reader.novel.drawer.menu.options.font_size.label")
        }}</span>
        <NamiButton
          :class="{
            invisible: fontRatio === (currTemplateDefault?.fontRatio ?? 1),
          }"
          button-type="danger"
          :icon="IconReload"
          small
          text
          pill
          @button-click="fontRatio = currTemplateDefault?.fontRatio ?? 1"
        />
        <span class="ml-auto font-medium"
          >{{ (fontRatio * getBaseFontSize()).round(1) }}px</span
        >
      </div>
      <NamiSliderNew
        class="!px-0 !h-auto"
        v-model="fontSizeStepped"
        :max="steps.length - 1"
        :min="0"
        no-labels
        no-bg
        no-tooltip
        hideNumber
        no-divider-margin
      />
      <TransitionAutoHeight :model-value="showAdvanced">
        <div class="space-y-2">
          <div class="flex items-center">
            <span class="font-medium">{{
              translate("reader.novel.drawer.menu.options.letter_spacing.label")
            }}</span>
            <NamiButton
              :class="{
                invisible:
                  letterSpacing === (currTemplateDefault?.letterSpacing ?? 0),
              }"
              button-type="danger"
              :icon="IconReload"
              small
              text
              pill
              @button-click="
                letterSpacing = currTemplateDefault?.letterSpacing ?? 0
              "
            />
            <span class="ml-auto font-medium">
              {{
                translate(
                  "reader.novel.drawer.menu.options.letter_spacing.value",
                  (letterSpacing * 100).round(),
                )
              }}
            </span>
          </div>
          <NamiSliderNew
            class="!px-0 !h-auto"
            v-model="letterSpacing"
            :max="1"
            :min="-1"
            continuous
            no-labels
            no-bg
            no-tooltip
          />
          <div class="flex items-center">
            <span class="font-medium">{{
              translate("reader.novel.drawer.menu.options.word_spacing.label")
            }}</span>
            <NamiButton
              :class="{
                invisible:
                  wordSpacing === (currTemplateDefault?.wordSpacing ?? 0),
              }"
              button-type="danger"
              :icon="IconReload"
              small
              text
              pill
              @button-click="
                wordSpacing = currTemplateDefault?.wordSpacing ?? 0
              "
            />
            <span class="ml-auto font-medium">
              {{
                translate(
                  "reader.novel.drawer.menu.options.word_spacing.value",
                  ((wordSpacing / 3) * 100).round(),
                )
              }}
            </span>
          </div>
          <NamiSliderNew
            class="!px-0 !h-auto"
            v-model="wordSpacing"
            :max="3"
            :min="-3"
            continuous
            no-labels
            no-bg
            no-tooltip
          />
          <div class="flex items-center">
            <span class="font-medium">{{
              translate("reader.novel.drawer.menu.options.line_height.label")
            }}</span>
            <NamiButton
              :class="{
                invisible:
                  lineHeightMult === (currTemplateDefault?.lineHeight ?? 1.5),
              }"
              button-type="danger"
              :icon="IconReload"
              small
              text
              pill
              @button-click="
                lineHeightMult = currTemplateDefault?.lineHeight ?? 1.5
              "
            />
            <span class="ml-auto font-medium">
              {{
                translate(
                  "reader.novel.drawer.menu.options.line_height.value",
                  (lineHeightMult / 1.5).round(2),
                )
              }}
            </span>
          </div>
          <NamiSliderNew
            class="!px-0 !h-auto"
            v-model="lineHeightMult"
            :max="3.75"
            :min="0.75"
            continuous
            no-labels
            no-bg
            no-tooltip
          />
          <div class="flex items-center">
            <span class="font-medium">{{
              translate("reader.novel.drawer.menu.options.margin.label")
            }}</span>
            <NamiButton
              :class="{ invisible: settingsModel.textMargin === 0.5 }"
              button-type="danger"
              :icon="IconReload"
              small
              text
              pill
              @button-click="settingsModel.textMargin = 0.5"
            />
            <span class="ml-auto font-medium">
              {{
                translate("reader.novel.drawer.menu.options.margin.value", {
                  count: textMargin,
                })
              }}
            </span>
          </div>
          <NamiSliderNew
            class="!px-0 !h-auto"
            v-model="settingsModel.textMargin"
            :max="1"
            :min="0"
            continuous
            no-labels
            no-bg
            no-tooltip
          />
          <div class="flex items-center justify-between">
            <span class="font-medium">{{
              translate("reader.novel.drawer.menu.options.bold.label")
            }}</span>
            <NamiSwitch v-model="bold" />
          </div>
          <div class="flex items-center justify-between">
            <span class="font-medium">{{
              translate("reader.novel.drawer.menu.options.justify.label")
            }}</span>
            <NamiSwitch v-model="justify" />
          </div>
        </div>
      </TransitionAutoHeight>
      <NamiButton
        class="mt-2"
        button-type="secondary"
        text
        pill
        :icon="showAdvanced ? IconChevronUp : IconChevronDown"
        @button-click="showAdvanced = !showAdvanced"
        block
      >
        {{ translate("reader.novel.drawer.menu.button.advanced") }}
      </NamiButton>
    </div>
    <ReportModal
      v-if="currentChapterId"
      v-model="showReportModal"
      entityType="novel_chapter"
      :entityId="currentChapterId"
    />
  </ReaderDrawer>
</template>

<script setup lang="ts">
import {
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
  IconChevronUp,
  IconDots,
  IconFlag,
  IconLink,
  IconRefresh,
  IconReload,
} from "@tabler/icons-vue";
import type {
  NovelSettingsInterface,
  NovelSettingsType,
} from "~/src/novel/reader";
import { getNovelThemes } from "~/src/novel/themes";
import deepEqual from "deep-eql";
import type { TitleChapters, TitleEntity } from "~/src/api";
import { getNovelReaderFontsForLanguage } from "~/src/novel/style";

const config = useRuntimeConfig();
const { locale, t: translate } = useI18n();
const href = useHref();
const shortUrl = useShortUrl();
const loggedIn = useIsLoggedIn();
const appStore = useAppStore();

const showAdvanced = ref(false);
const showReportModal = ref(false);

const { title, chapterIdSequence, currentChapterIndex } = defineProps<{
  profiles: NovelSettingsType[];
  currentChapterIndex?: number;
  chapterIdSequence?: string[];
  hasNextChapter?: boolean;
  hasPrevChapter?: boolean;
  aggregate?: TitleChapters;
  title?: TitleEntity;
  loading?: boolean;
  solo?: boolean;
}>();

const emit = defineEmits<{
  (e: "switchChapter", chapterId: string): void;

  (e: "nextChapter"): void;

  (e: "prevChapter"): void;

  (e: "resetProfile", index: number): void;
}>();

const resetProfile = async () => {
  const res = await appStore.prompt(
    "Are you sure you want to reset this profile?",
    {
      icon: "confused",
      buttons: {
        cancel: {
          buttonType: "primary",
          buttonText: "No",
        },
        confirm: {
          buttonType: "secondary",
          buttonText: "Yes",
        },
      },
    },
  );

  if (res === "confirm") emit("resetProfile", profileIndex.value);
};

const currentChapterId = computed(
  () => chapterIdSequence?.[currentChapterIndex ?? 0],
);

const fonts = computed(() => {
  const availableFonts = getNovelReaderFontsForLanguage(locale.value);
  availableFonts.unshift({
    text: "fonts.appearance.default",
    value: "inherit",
    appearance: "fonts.appearance.default",
    link: undefined,
  });
  return availableFonts.map((font) => ({
    ...font,
    text: translate(font.appearance),
    props: {
      baseFontRatio: font.baseFontRatio,
      appearance: font.appearance,
    },
  }));
});

useHead({
  link: () =>
    fonts.value
      .map(({ link }) => {
        if (link)
          return {
            href: link,
            rel: "stylesheet",
          };
      })
      .filter(onlyTruthys),
});

const defaultTemplates = getNovelThemes();
const currTemplateDefault = computed(() => {
  return defaultTemplates.find((t) => t.name === currentTemplate.value.name);
});

const { title: titleTitle } = parseTitleForDisplay(computed(() => title));

const profileIndex = defineModel<number>("profileIndex", { required: true });

const settingsModel = defineModel<NovelSettingsType>({ required: true });
const currentTemplate = computed(
  () => settingsModel.value.templates[settingsModel.value.templateIndex],
);

const availableThemes = computed(() =>
  settingsModel.value.templates.map((template) => {
    const templateDefault = defaultTemplates.find(
      (t) => t.name === template.name,
    );
    return {
      ...template,
      modified: !deepEqual(templateDefault, template),
    };
  }),
);

const menuOpen = defineModel<boolean>("menuOpen", { default: false });
const secondaryMenuOpen = defineModel<
  NovelSettingsInterface["secondaryMenuOpen"]
>("secondaryMenuOpen", { default: false });

const commentsOpen = computed({
  get: () => secondaryMenuOpen.value === "comments",
  set: (v) => {
    if (v) secondaryMenuOpen.value = "comments";
    else secondaryMenuOpen.value = false;
  },
});

const fontFamily = computed({
  get: () => currentTemplate.value.fontFamily ?? "inherit",
  set: (v) => (currentTemplate.value.fontFamily = v),
});
const lineHeightMult = computed({
  get: () => currentTemplate.value.lineHeight ?? 1.5,
  set: (v) => (currentTemplate.value.lineHeight = v.round(4)),
});
const letterSpacing = computed({
  get: () => currentTemplate.value.letterSpacing ?? 0,
  set: (v) => (currentTemplate.value.letterSpacing = v.round(4)),
});
const wordSpacing = computed({
  get: () => currentTemplate.value.wordSpacing ?? 0,
  set: (v) => (currentTemplate.value.wordSpacing = v.round(4)),
});
const fontRatio = computed({
  get: () => currentTemplate.value.fontRatio ?? 16,
  set: (v) => (currentTemplate.value.fontRatio = v),
});
const justify = computed({
  get: () => currentTemplate.value.justify ?? false,
  set: (v) => (currentTemplate.value.justify = v),
});
const bold = computed({
  get: () => currentTemplate.value.bold ?? false,
  set: (v) => (currentTemplate.value.bold = v),
});
const textMargin = computed(() => {
  if (settingsModel.value.textMargin - 0.5 > 0)
    return `+${(((settingsModel.value.textMargin - 0.5) / 5) * 100).round()}`;
  return (((settingsModel.value.textMargin - 0.5) / 5) * 100)
    .round()
    .toString();
});

const getBaseFontSize = () => {
  if (!document || !window) return 16;
  const htmlElement = document.documentElement;
  return parseInt(window.getComputedStyle(htmlElement).fontSize);
};

const steps = [8, 9, 9.5, 10, 10.5, 12, 14, 16, 20, 24, 28, 32, 36, 40, 48];
const fontSizeStepped = computed({
  get() {
    const index = steps.indexOf(fontRatio.value * getBaseFontSize());
    if (index < 0) return 4;
    return index;
  },
  set(value) {
    fontRatio.value = steps[value] / getBaseFontSize();
  },
});

const { action: getPageLink } = useAction2(async () => {
  if (!currentChapterId.value) return;
  await copyTextToClipboard(shortUrl.novelChapter.id(currentChapterId.value));
});
</script>

<style scoped lang="postcss"></style>
